import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from './AnimatedSection';
import { usePublicSettings } from '../context/PublicSettingsContext';
import { FARM_ADDRESS_LINE, FARM_ADDRESS_CITY } from '../constants/address';
import './Footer.css';

const DEFAULT_HOURS = [
  { label: 'Mon–Fri', value: '2–6 PM' },
  { label: 'Sat', value: '10 AM–6 PM' },
];

const Footer = () => {
  const { settings } = usePublicSettings();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const hoursShort = settings?.business_hours?.lines?.length
    ? settings.business_hours.lines.slice(0, 2).map(l => `${l.label}: ${l.value}`).join(' · ')
    : DEFAULT_HOURS.map(l => `${l.label} ${l.value}`).join(' · ');

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 4000);
    }
  };

  return (
    <footer className="footer">
      <div className="footer__wave">
        <svg viewBox="0 0 1440 100" preserveAspectRatio="none">
          <path d="M0,40 C360,100 720,0 1080,60 C1260,90 1380,50 1440,40 L1440,100 L0,100 Z" fill="var(--green-dark)" />
        </svg>
      </div>

      <div className="footer__main">
        <div className="container">
          <div className="footer__grid">
            <AnimatedSection className="footer__brand" direction="up">
              <img src="/logo-cropped.png" alt="Cory Blossom" className="footer__logo" />
              <p className="footer__tagline">
                Fresh flowers from the Finger Lakes, grown with love.
              </p>
              <p className="footer__address">
                {FARM_ADDRESS_LINE}<br />
                {FARM_ADDRESS_CITY}
              </p>
              <p className="footer__legal">
                <Link to="/privacy">Privacy Policy</Link>
                <span className="footer__legal-sep" aria-hidden="true">·</span>
                <Link to="/terms">Terms of Service</Link>
              </p>
              <div className="footer__social">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </a>
                <a href="https://pinterest.com" target="_blank" rel="noopener noreferrer" aria-label="Pinterest">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
                </a>
              </div>
            </AnimatedSection>

            <AnimatedSection className="footer__nav" direction="up" delay={0.1}>
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/shop">Shop</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/gallery">Gallery</Link></li>
                <li><Link to="/contact">Contact</Link></li>
              </ul>
            </AnimatedSection>

            <AnimatedSection className="footer__nav" direction="up" delay={0.2}>
              <h4>Shop</h4>
              <ul>
                <li><Link to="/shop/spring-wrapped-subscription">Subscriptions</Link></li>
                <li><Link to="/shop/wrapped-bouquet-medium">Wrapped Bouquets</Link></li>
                <li><Link to="/shop/arranged-bouquet-medium">Arranged Bouquets</Link></li>
                <li><Link to="/shop/dahlia-tubers">Dahlia Tubers</Link></li>
                <li><Link to="/weddings">Weddings</Link></li>
              </ul>
            </AnimatedSection>

            <AnimatedSection className="footer__newsletter" direction="up" delay={0.3}>
              <h4>Stay in Bloom</h4>
              <p>Subscribe for seasonal updates, new arrivals, and exclusive offers.</p>
              <form className="footer__form" onSubmit={handleSubscribe}>
                <input
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {subscribed ? '✓ Subscribed!' : 'Subscribe'}
                </motion.button>
              </form>
            </AnimatedSection>
          </div>

          <div className="footer__meta">
            <p className="footer__hours-line">{hoursShort}</p>
            <p className="footer__copyright">&copy; {new Date().getFullYear()} Cory Blossom. All rights reserved.</p>
            <p className="footer__watermark">
              Made by{' '}
              <a
                href="https://shawnymedia.org"
                target="_blank"
                rel="noopener noreferrer"
              >
                ShawnyMedia
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
